import { Box } from '@mui/material';
import {
  getPurchaseOrderDetails,
  getPurchaseOrders,
} from 'lib/api/publicMarket';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import { PURCHASE_ORDER_STATES } from 'lib/consts';
import TableWithPagination, {
  Header,
} from 'lib/designSystem/TableWithPagination';
import { errorHandler } from 'lib/functions/errors';
import { PurchaseOrder, PurchaseOrderType } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { showPurchaseOrderModal } from 'services/redux/purchaseOrderModalSlice';

const BASE_PAGINATION = {
  page: 1,
  pageSize: 25,
  total: 0,
  totalPages: 0,
};

const HEADERS: Header[] = [
  {
    label: 'Código',
    key: 'externalCode',
    customComponent: (value: string) => <Box sx={{ py: 1 }}>{value}</Box>,
  },
  { label: 'Nombre', key: 'name' },
  { label: 'Tipo', key: 'type' },
  { label: 'Estado', key: 'status' },
  { label: 'Mandante', key: 'buyerId' },
  { label: 'Proveedor', key: 'supplierId' },
  { label: 'Fecha Creación', key: 'creationDate', type: 'date' },
  { label: 'Monto Total', key: 'total', type: 'money' },
];

const PurchaseOrders = () => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PurchaseOrder[]>([]);
  const [pagination, setPagination] = useState(BASE_PAGINATION);
  const [externalCode, setExternalCode] = useState<string>('');
  const [selectedBuyer, setSelectedBuyer] = useState<string>('');
  const [initialDateStr, setInitialDateStr] = useState<string | null>(null);
  const [finalDateStr, setFinalDateStr] = useState<string | null>(null);
  const [status, setStatus] = useState<{ value: string; label: string }[]>([]);
  const [types, setTypes] = useState<
    { value: PurchaseOrderType; label: string }[]
  >([]);

  const buildPurchaseOrdersQuery = (override?: {
    page?: number;
    pageSize?: number;
  }): string => {
    const params = new URLSearchParams();
    if (!id) throw new Error('Supplier ID is required');
    params.append('supplierId', id);
    if (externalCode) params.append('externalCode', externalCode);
    if (selectedBuyer) params.append('buyerId', selectedBuyer);
    if (types.length > 0)
      params.append('types', types.map((t) => t.value).join(','));
    if (status.length > 0)
      params.append('status', status.map((s) => s.value).join(','));
    if (initialDateStr) params.append('startCreationDate', initialDateStr);
    if (finalDateStr) params.append('endCreationDate', finalDateStr);
    const pageToUse = override?.page ?? pagination.page;
    const pageSizeToUse = override?.pageSize ?? pagination.pageSize;
    if (pageToUse) params.append('page', String(pageToUse));
    if (pageSizeToUse) params.append('pageSize', String(pageSizeToUse));
    return params.toString();
  };

  const fetchData = async (params?: { page: number; pageSize: number }) => {
    try {
      setLoading(true);
      const pageToUse = params?.page ?? pagination.page;
      const pageSizeToUse = params?.pageSize ?? pagination.pageSize;
      const queryString = buildPurchaseOrdersQuery({
        page: pageToUse,
        pageSize: pageSizeToUse,
      });
      const response = await getPurchaseOrders(dispatch, queryString);
      setData(response?.data ?? []);
      setPagination(response?.pagination ?? BASE_PAGINATION);
      return {
        data: response?.data ?? [],
        pagination: response?.pagination ?? BASE_PAGINATION,
      };
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (rowId: string) => {
    try {
      if (!rowId) throw new Error('Purchase Order ID is required');
      const details = await getPurchaseOrderDetails(dispatch, rowId);
      if (details) dispatch(showPurchaseOrderModal(details));
    } catch (error) {
      errorHandler(dispatch, error);
    }
  };

  const filters: Filter[] = [
    {
      type: 'text',
      label: 'Código',
      value: externalCode,
      setter: setExternalCode,
    },
    {
      type: 'autocomplete',
      label: 'Mandante',
      key: 'buyerId',
      value: selectedBuyer,
      setter: setSelectedBuyer,
    },
    {
      type: 'date',
      label: 'Fecha inicio',
      dateKey: 'startDate',
      value: initialDateStr,
      setter: setInitialDateStr,
    },
    {
      type: 'date',
      label: 'Fecha fin',
      dateKey: 'endDate',
      value: finalDateStr,
      setter: setFinalDateStr,
    },
    {
      type: 'multiple',
      label: 'Tipo',
      value: types,
      setter: setTypes,
      options: Object.values(PurchaseOrderType).map((t) => ({
        value: t,
        label: t,
      })),
    },
    {
      type: 'multiple',
      label: 'Estado',
      value: status,
      setter: setStatus,
      options: PURCHASE_ORDER_STATES.map((s) => ({
        value: String(s.value),
        label: s.label,
      })),
    },
  ];

  useEffect(() => {
    void fetchData();
  }, []);

  return (
    <Box sx={{ py: 2, position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: -36, right: 0 }}>
        <GlobalSearcher
          filters={filters}
          triggerSearch={() => fetchData()}
          dispatch={dispatch}
        />
      </Box>
      <TableWithPagination
        data={data}
        setData={setData}
        dataHeaders={HEADERS}
        loading={loading}
        pagination={pagination}
        setPagination={setPagination}
        searchData={fetchData}
        exportToFile="xlsx"
        handleClickRow={handleRowClick}
      />
    </Box>
  );
};

export default PurchaseOrders;
